import React, { useState } from "react";
import Select, { OptionsOrGroups } from "react-select"
import { useEffectOnce } from "@lib-shared/hooks";

export type Option = {
    label: string,
    value: string
}

type ChangeHandler = (option?: Option, name?: string) => void;

type SelectXProps = React.DetailedHTMLProps<
    React.SelectHTMLAttributes<HTMLSelectElement>,
    HTMLSelectElement
> & {
    options: Option[],
    name: string,
    label: string,
    cssName?: string,
    initialValue?: string,
    labelCssName?: string,
    placeHolder?: string,
    onHandleChange: ChangeHandler
    onFocus?: any,
    onBlur?: any
    isClearable?: boolean,
    isSearchable?: boolean
    borderBottom?: string,
    useFloaty?: boolean
};

const borderColour = "#6c7177";
const activeBorderColour = "#0095da";

export const SelectX = (props: SelectXProps) => {
    const [hasValue, setHasValue] = useState(false);

    const {
        name,
        options,
        label,
        labelCssName,
        initialValue = undefined,
        onHandleChange,
        onFocus,
        onBlur,
        placeHolder,
        disabled = false,
        isClearable = true,
        isSearchable = false,
        borderBottom = "1px solid " + borderColour,
        useFloaty = true
    } = props;

    const selectOptions = options as OptionsOrGroups<any, any>;
    const defaultValue = selectOptions.find(x => x.value === initialValue);

    useEffectOnce(() => {
        if (defaultValue) {
            setHasValue(true);
        }
    });

    const onChange = (option: Option, am: any) => {
        setHasValue(option !== undefined && option !== null);
        onHandleChange(option, name);
    }

    const labelCss = ((labelCssName ?? "") + (hasValue ? " visible-x" : " hidden-x")).trim();

    //See https://react-select.com/styles for styling guide
    //The recommended way is to use the styles prop
    return (<>
        {!useFloaty && <label htmlFor={name} className={labelCss}>{label}</label>}

        <Select
            id={name}
            classNamePrefix="react-select"
            defaultValue={defaultValue}
            placeholder={placeHolder ?? label}
            isClearable={isClearable}
            isSearchable={isSearchable}
            onChange={onChange}
            isDisabled={disabled}
            onFocus={onFocus}
            onBlur={onBlur}
            styles={{
                control: (baseStyles, state) => ({
                    ...baseStyles,
                    border: 0,
                    borderBottom: borderBottom,
                    borderRadius: 0,
                    borderBottomColor: state.isFocused ? activeBorderColour : borderColour,
                    boxShadow: "none",
                    ":hover": {
                        borderBottomColor: state.isFocused ? activeBorderColour : borderColour
                    }
                }),
                indicatorSeparator: (base, props) => ({
                    display: "none"
                }),
                menu: (baseStyles, state) => ({
                    ...baseStyles,
                    borderRadius: 0
                }),
                menuList: (baseStyles, state) => ({
                    ...baseStyles,
                    boxShadow: "0 0 5px rgba(0,0,0,.3)",
                    borderRadius: 0
                }),
                option: (baseStyles, state) => ({
                    color: "#00aeef",
                    fontSize: "11px",
                    padding: "15px 20px",
                    borderRadius: 0,
                    cursor: "pointer",
                    ":hover": {
                        backgroundColor: "rgba(10,18,29,.051)",
                        fontWeight: 600
                    }
                })
            }}
            options={selectOptions} />

        {useFloaty && <label htmlFor={name} className={labelCss}>{label}</label>}
    </>);
}

export default SelectX;
